import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ShieldCheck, Check, X, Info } from 'lucide-react';
import toast from 'react-hot-toast';
import Layout from '../../components/layout/AppLayout';
import Header from '../../../shared/components/layout/Header';
import { useSmartBack } from '../../../shared/navigation';
import Loader from '../../../shared/components/ui/Loader';
import Modal from '../../../shared/components/ui/Modal';
import {
  getPendingLabourPayments,
  decideLabourPayment
} from '../../services/api/implementation/impl';
import { Callout } from '../../../shared/components/ui/primitives';

/**
 * Payments held for a second person.
 *
 * A held payment has written NO ledger row and has moved no balance — that is
 * the point of holding it. Until somebody approves, the workman's outstanding
 * figure is unchanged and no cash should have left the office.
 *
 * The server refuses a decision from the person who recorded the payment. This
 * screen does not try to guess who that is; it lets the attempt fail and shows
 * the reason, because the alternative is a client-side rule that drifts from
 * the server's and hides rows somebody should be able to see.
 */

const PaymentApprovals = () => {
  const { onBack } = useSmartBack({ fallback: '/builder/workforce?tab=labour' });
  const queryClient = useQueryClient();
  const [rejecting, setRejecting] = useState(null);

  const { data, isFetching } = useQuery({
    queryKey: ['pendingLabourPayments'],
    queryFn: () => getPendingLabourPayments()
  });

  const decide = useMutation({
    mutationFn: ({ id, ...payload }) => decideLabourPayment(id, payload),
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({ queryKey: ['pendingLabourPayments'] });
      queryClient.invalidateQueries({ queryKey: ['labourDues'] });
      queryClient.invalidateQueries({ queryKey: ['labourLedger'] });
      setRejecting(null);
      toast.success(variables.approve === false ? 'Payment rejected' : 'Payment approved');
    },
    onError: (err) =>
      toast.error(err?.response?.data?.message || 'Could not record the decision')
  });

  const rows = data?.data ?? [];
  const meta = data?.meta ?? {};
  const notApprover = meta.can_approve === false;

  return (
    <Layout>
      <Header backArrow onBack={onBack} data={{ name: 'Payment Approvals' }} />

      <div className="space-y-4 p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <Callout tone="info" collapsible icon={<Info size={14} />} title="About held payments" className="min-w-0 flex-1 sm:max-w-2xl">
            Payments at or above your approval amount wait here. The workman&apos;s balance doesn&apos;t change until one is approved.
          </Callout>
          {rows.length > 0 && (
            <div className="rounded-control border border-warning-border bg-warning-bg px-3 py-2 text-xs">
              <div className="text-[11px] uppercase tracking-wide text-warning-fg">
                Awaiting approval
              </div>
              <div className="text-lg font-semibold tabular-nums text-warning-fg">
                {money(meta.total_amount)}
              </div>
            </div>
          )}
        </div>

        {isFetching ? (
          <Loader />
        ) : rows.length === 0 ? (
          <div className="rounded-lg border border-dashed border-gray-300 bg-white py-10 text-center">
            <ShieldCheck size={28} className="mx-auto text-success-fg" />
            <p className="mt-2 text-sm text-gray-600">
              Nothing waiting. Payments below the threshold post straight
              through, and a builder with no threshold set has no approval step
              at all.
            </p>
          </div>
        ) : (
          <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-gray-600">
                  <tr>
                    <Th>Workman</Th>
                    <Th className="text-right">Amount</Th>
                    <Th>Dated</Th>
                    <Th>Mode</Th>
                    <Th>Recorded by</Th>
                    <Th />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.id} className="border-t border-gray-100">
                      <Td>
                        <div className="font-medium text-gray-900">
                          {row.worker?.name ?? '—'}
                        </div>
                        {row.project && (
                          <div className="text-xs text-gray-500">{row.project}</div>
                        )}
                      </Td>
                      <Td className="text-right font-semibold tabular-nums">
                        {money(row.amount)}
                      </Td>
                      <Td className="whitespace-nowrap">{shortDate(row.paid_on)}</Td>
                      <Td className="text-xs">
                        {titleCase(row.mode)}
                        {row.reference && (
                          <div className="text-gray-500">{row.reference}</div>
                        )}
                      </Td>
                      <Td className="text-xs text-gray-600">{row.recorded_by ?? '—'}</Td>
                      <Td>
                        {/* The person who recorded a payment cannot approve it
                            (the server refuses). can_decide says so up front;
                            an older server that does not send it leaves the
                            buttons live, as before. */}
                        {row.can_decide === false && (
                          <div className="mb-1 text-xs text-gray-500">
                            {/* meta.can_approve false: the viewer is not the
                                owner or an assigned wage-payment approver. */}
                            {notApprover
                              ? 'Approved by the owner or the assigned approver'
                              : 'Recorded by you — needs another approver'}
                          </div>
                        )}
                        <div className="flex gap-2">
                          <button
                            type="button"
                            disabled={decide.isPending || row.can_decide === false}
                            title={
                              row.can_decide === false
                                ? notApprover
                                  ? 'Approved by the owner or the assigned approver'
                                  : 'You recorded this payment, so someone else must approve it'
                                : undefined
                            }
                            onClick={() => decide.mutate({ id: row.id, approve: true })}
                            className="flex items-center gap-1 rounded bg-brand-700 px-2.5 py-1 text-xs text-white disabled:cursor-not-allowed disabled:opacity-40"
                          >
                            <Check size={12} /> Approve
                          </button>
                          <button
                            type="button"
                            disabled={decide.isPending || row.can_decide === false}
                            title={
                              row.can_decide === false
                                ? notApprover
                                  ? 'Decided by the owner or the assigned approver'
                                  : 'You recorded this payment, so someone else must decide it'
                                : undefined
                            }
                            onClick={() => setRejecting(row)}
                            className="flex items-center gap-1 rounded border border-danger-border px-2.5 py-1 text-xs text-danger-fg disabled:cursor-not-allowed disabled:opacity-40"
                          >
                            <X size={12} /> Reject
                          </button>
                        </div>
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {rejecting && (
        <RejectModal
          payment={rejecting}
          busy={decide.isPending}
          onClose={() => setRejecting(null)}
          onSubmit={(reason) =>
            decide.mutate({ id: rejecting.id, approve: false, reason })
          }
        />
      )}
    </Layout>
  );
};

const RejectModal = ({ payment, busy, onClose, onSubmit }) => {
  const [reason, setReason] = useState('');
  return (
    <Modal
      name={`Reject ${money(payment.amount)} to ${payment.worker?.name ?? 'workman'}`}
      onClose={onClose}
      width="460px"
    >
      <div className="space-y-3">
        <p className="text-sm text-gray-600">
          The payment record stays, marked rejected. That somebody tried to
          disburse this is worth keeping.
        </p>
        <div>
          <div className="mb-1 text-xs font-medium text-gray-600">Reason</div>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            placeholder="Why this is not being paid"
          />
        </div>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={busy || !reason.trim()}
            onClick={() => onSubmit(reason.trim())}
            className="rounded-lg bg-danger px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
          >
            {busy ? 'Recording…' : 'Reject payment'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

const Th = ({ children, className = '' }) => (
  <th className={`whitespace-nowrap px-4 py-3 text-left font-medium ${className}`}>
    {children}
  </th>
);
const Td = ({ children, className = '' }) => (
  <td className={`px-4 py-3 align-top ${className}`}>{children}</td>
);

function money(value) {
  const n = Number(value ?? 0);
  if (!Number.isFinite(n)) return '₹0';
  return `₹${n.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
}
function shortDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' });
}
function titleCase(value) {
  if (!value) return '—';
  return value
    .toLowerCase()
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

export default PaymentApprovals;
